import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, MessageCircle, Pencil, Plus, Trash2, UserCheck, Users, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const emptyForm = { nombre: "", telefono: "", proxima_cita: "", estado: "activo", notas: "" };
const todayStr = () => new Date().toISOString().slice(0, 10);

const waPatient = (phone, nombre) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const num = digits.length === 8 ? `507${digits}` : digits;
  const text = `Hola ${String(nombre).split(" ")[0]}, soy Elba Rojas, tu psicóloga. Te escribo sobre tu proceso terapéutico.`;
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
};

const FollowUpBadge = ({ date }) => {
  if (!date) return <span className="text-stonemute">—</span>;
  const today = todayStr();
  const label = new Date(`${date}T12:00:00`).toLocaleDateString("es", { day: "2-digit", month: "short" });
  let cls = "bg-sand text-ink/70 border border-line";
  let prefix = "";
  if (date < today) { cls = "bg-red-50 text-red-700"; prefix = "Venció · "; }
  else if (date === today) { cls = "bg-green-50 text-green-700"; prefix = "Hoy · "; }
  return <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap ${cls}`}>{prefix}{label}</span>;
};

export default function Patients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/patients");
      setItems(res.data);
    } catch {
      toast.error("No se pudieron cargar los pacientes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const sorted = useMemo(() => {
    const withDate = items.filter((p) => p.proxima_cita).sort((a, b) => a.proxima_cita.localeCompare(b.proxima_cita));
    const without = items.filter((p) => !p.proxima_cita);
    return [...withDate, ...without];
  }, [items]);

  const activos = items.filter((p) => p.estado === "activo").length;
  const proximos7 = items.filter((p) => {
    if (!p.proxima_cita) return false;
    const diff = (new Date(`${p.proxima_cita}T12:00:00`) - new Date(`${todayStr()}T12:00:00`)) / 86400000;
    return diff >= 0 && diff <= 7;
  }).length;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ nombre: p.nombre, telefono: p.telefono || "", proxima_cita: p.proxima_cita || "", estado: p.estado, notas: p.notas || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim() || null,
        proxima_cita: form.proxima_cita || null,
        estado: form.estado,
        notas: form.notas.trim() || null,
      };
      if (editingId) {
        await api.put(`/patients/${editingId}`, body);
        toast.success("Paciente actualizado");
      } else {
        await api.post("/patients", body);
        toast.success("Paciente registrado");
      }
      resetForm();
      fetchItems();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/patients/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Paciente eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-6" data-testid="patients-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { icon: Users, label: "Pacientes activos", value: activos, testid: "stat-active-patients" },
          { icon: CalendarClock, label: "Seguimientos en 7 días", value: proximos7, testid: "stat-upcoming-followups" },
        ].map(({ icon: Icon, label, value, testid }) => (
          <div key={testid} className="bg-white border border-line rounded-lg p-5 flex items-center gap-4" data-testid={testid}>
            <span className="grid place-items-center h-11 w-11 rounded-full bg-sand border border-line">
              <Icon className="h-5 w-5 text-sage" />
            </span>
            <div>
              <p className="font-serif text-3xl leading-none">{value}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-stonemute mt-1">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} data-testid="patient-form" className="bg-white border border-line rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="font-serif text-lg">{editingId ? "Editar paciente" : "Registrar paciente"}</p>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={resetForm} data-testid="cancel-edit-patient" className="text-stonemute hover:text-ink hover:bg-sand">
              <X className="h-4 w-4 mr-1" /> Cancelar
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="pt-nombre" className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Nombre *</Label>
            <Input id="pt-nombre" required minLength={2} value={form.nombre} onChange={set("nombre")} placeholder="Nombre del paciente" data-testid="patient-nombre-input" className="mt-1.5 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
          <div>
            <Label htmlFor="pt-telefono" className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Teléfono / WhatsApp</Label>
            <Input id="pt-telefono" type="tel" value={form.telefono} onChange={set("telefono")} placeholder="Opcional" data-testid="patient-telefono-input" className="mt-1.5 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
          <div>
            <Label htmlFor="pt-proxima" className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Cita de seguimiento</Label>
            <Input id="pt-proxima" type="date" value={form.proxima_cita} onChange={set("proxima_cita")} data-testid="patient-proxima-input" className="mt-1.5 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Estado</Label>
            <Select value={form.estado} onValueChange={(v) => setForm((f) => ({ ...f, estado: v }))}>
              <SelectTrigger data-testid="patient-estado-select" className="mt-1.5 h-11 bg-sand/50 border-line focus:ring-sage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-line">
                <SelectItem value="activo" data-testid="patient-estado-activo">Activo</SelectItem>
                <SelectItem value="alta" data-testid="patient-estado-alta">De alta</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2">
            <Label htmlFor="pt-notas" className="text-xs uppercase tracking-[0.15em] font-bold text-ink/70">Notas</Label>
            <Input id="pt-notas" value={form.notas} onChange={set("notas")} placeholder="Motivo, avances, lo que necesites recordar" data-testid="patient-notas-input" className="mt-1.5 h-11 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
        </div>
        <Button type="submit" disabled={saving} data-testid="submit-patient-form" className="mt-5 rounded-full bg-sage text-white text-sm font-bold hover:bg-sage-dark">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" /> {editingId ? "Guardar cambios" : "Agregar paciente"}</span>}
        </Button>
      </form>

      <div className="bg-white border border-line rounded-lg overflow-hidden">
        <p className="font-serif text-lg px-5 py-4 border-b border-line">Pacientes</p>
        {loading ? (
          <div className="p-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sage" /></div>
        ) : items.length === 0 ? (
          <p className="p-10 text-center text-sm text-stonemute" data-testid="empty-patients-message">Aún no hay pacientes registrados.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="patients-table">
              <TableHeader>
                <TableRow className="border-line">
                  <TableHead>Paciente</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Seguimiento</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Notas</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((p) => (
                  <TableRow key={p.id} className="border-line" data-testid={`patient-row-${p.id}`}>
                    <TableCell className="font-medium whitespace-nowrap">{p.nombre}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {p.telefono ? (
                        <span className="inline-flex items-center gap-2">
                          {p.telefono}
                          <a href={waPatient(p.telefono, p.nombre)} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-patient-button-${p.id}`}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[#1eb85a] transition-colors hover:bg-green-50">
                            <MessageCircle className="h-4 w-4" />
                          </a>
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell><FollowUpBadge date={p.proxima_cita} /></TableCell>
                    <TableCell>
                      <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${p.estado === "activo" ? "bg-green-50 text-green-700" : "bg-sand text-stonemute border border-line"}`} data-testid={`patient-estado-badge-${p.id}`}>
                        {p.estado === "activo" ? "Activo" : "De alta"}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate text-stonemute">{p.notas || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(p)} data-testid={`edit-patient-button-${p.id}`} className="text-stonemute hover:text-sage hover:bg-sand">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(p.id)} data-testid={`delete-patient-button-${p.id}`} className="text-stonemute hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
